import React from "react";
import { useNavigate } from "react-router-dom";

import DoctorMap from "@/components/maps/DoctorMap.jsx";
import PharmacyResultCard from "@/components/pharmacies/PharmacyResultCard.jsx";
import { getAfriMedMapStyle } from "@/constants/mapStyles.js";

import SearchBar from "@/sot/primitives/SearchBar.jsx";
import AfriPill from "@/sot/primitives/AfriPill.jsx";
import useGeolocation from "@/hooks/useGeolocation.js";
import LocationConsentDialog from "@/components/location/LocationConsentDialog.jsx";
import { useLanguage } from "@/contexts/LanguageContext.jsx";
import { useAuth } from "@/contexts/AuthContext.jsx";
import { useTheme } from "@/contexts/ThemeContext.jsx";

import { useAppointmentsFilters } from "@/pages/appointments/AppointmentsFiltersContext.jsx";
import AppointmentsResultsRail from "@/pages/appointments/AppointmentsResultsRail.jsx";

const PHARMACY_IMAGES = [
  "/assets/images/pharmacies/Okavango Pharmacy.webp",
  "/assets/images/pharmacies/Miam pharmacy.jpg",
  "/assets/images/pharmacies/Bettercare Pharmacy-1.jpg",
  "/assets/images/pharmacies/Reld Med Pharmacy.jpg",
  "/assets/images/pharmacies/Dis-Chem Pharmacy.jpg",
  "/assets/images/pharmacies/Ok Plus Pharmacy.jpg",
];

const DEMO_CENTER = { lat: -24.6282, lng: 25.9231 };
const PHARMACY_CSV_PATH = "/data/pharmacy_facilities_sub_saharan.csv";

const ICONS = {
  back: "/icons/filter-doctors/back-arrow.svg",
  cancel: "/icons/doctor/cancel.svg",
  filter: "/icons/filter-doctors/date_range_filled-dark.svg",
  nearby: "/icons/filter-doctors/distance.svg",
};

function splitCsvLine(line = "") {
  const out = [];
  let cur = "";
  let q = false;

  for (let i = 0; i < line.length; i += 1) {
    const ch = line[i];

    if (ch === '"' && line[i + 1] === '"') {
      cur += '"';
      i += 1;
      continue;
    }
    if (ch === '"') {
      q = !q;
      continue;
    }
    if (ch === "," && !q) {
      out.push(cur);
      cur = "";
      continue;
    }
    cur += ch;
  }
  out.push(cur);

  return out.map((s) => String(s).trim());
}

function normalizeMatchText(value = "") {
  return String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();
}

function toggleInArray(arr, id) {
  if (!Array.isArray(arr)) return [id];
  return arr.includes(id) ? arr.filter((x) => x !== id) : [...arr, id];
}

function titleCase(value = "") {
  return String(value || "")
    .trim()
    .split(/\s+/)
    .map((word) => (word ? word[0].toUpperCase() + word.slice(1) : ""))
    .join(" ");
}

function toNum(value) {
  const n = Number(String(value ?? "").trim());
  return Number.isFinite(n) ? n : null;
}

function haversineKm(a, b) {
  const R = 6371;
  const dLat = ((b.lat - a.lat) * Math.PI) / 180;
  const dLng = ((b.lng - a.lng) * Math.PI) / 180;
  const lat1 = (a.lat * Math.PI) / 180;
  const lat2 = (b.lat * Math.PI) / 180;
  const s =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(s)));
}

function formatDistance(distanceKm) {
  const value = Number(distanceKm);
  if (!Number.isFinite(value)) return "—";
  if (value < 1) return `${Math.max(1, Math.round(value * 1000))} m`;
  if (value < 10) return `${value.toFixed(1)} km`;
  return `${Math.round(value)} km`;
}

function imageForIndex(i) {
  return (
    PHARMACY_IMAGES[i % PHARMACY_IMAGES.length] ||
    "/assets/images/pharmacies/Okavango Pharmacy.webp"
  );
}

function buildPharmacyAddress(row = {}) {
  const type = String(row?.afrimed_category || row?.facility_type || "").trim();
  const location = [row?.province, row?.Country].filter(Boolean).join(", ");
  return [type, location].filter(Boolean).join(" · ");
}

export default function PharmaciesPage() {
  const nav = useNavigate();
  const auth = useAuth();
  const { isDark } = useTheme();
  const { t } = useLanguage();
  const { filters, setFilters, openSheet } = useAppointmentsFilters();
  const geo = useGeolocation();
  const [query, setQuery] = React.useState("");
  const [loadingList, setLoadingList] = React.useState(false);
  const [error, setError] = React.useState("");
  const [rows, setRows] = React.useState([]);
  const [mapRef, setMapRef] = React.useState(null);
  const [selectedFacility, setSelectedFacility] = React.useState(null);
  const [highlightFacilityId, setHighlightFacilityId] = React.useState(null);
  const autoFitKeyRef = React.useRef("");

  const patientCountry = normalizeMatchText(auth?.profile?.country || "");
  const userCoords =
    geo?.coords?.lat != null && geo?.coords?.lng != null
      ? { lat: Number(geo.coords.lat), lng: Number(geo.coords.lng) }
      : null;
  const canShowUser = !!userCoords;

  React.useEffect(() => {
    if (geo?.consent === "always" && geo?.status === "granted") {
      geo.startWatch();
      return () => geo.stopWatch();
    }
    return undefined;
  }, [geo]);

  React.useEffect(() => {
    let alive = true;

    async function loadPharmacies() {
      setLoadingList(true);
      setError("");

      try {
        const res = await fetch(PHARMACY_CSV_PATH, { cache: "no-store" });
        if (!res.ok) throw new Error("Failed to load pharmacy facilities dataset.");

        const text = await res.text();
        const lines = text
          .replace(/^\uFEFF/, "")
          .split(/\r?\n/)
          .map((line) => line.trim())
          .filter(Boolean);
        if (lines.length < 2) throw new Error("Pharmacy facilities dataset is empty.");

        const headers = splitCsvLine(lines[0]);
        const parsed = lines
          .slice(1)
          .map((line, index) => {
            const cols = splitCsvLine(line);
            const row = {};
            headers.forEach((header, headerIndex) => {
              row[header] = cols[headerIndex];
            });

            const lat = toNum(row.latitude);
            const lng = toNum(row.longitude);
            if (lat == null || lng == null) return null;
            if (Math.abs(lat) < 0.000001 && Math.abs(lng) < 0.000001) return null;

            const name = String(row.facility_name || "").trim();
            if (!name) return null;

            return {
              id: `pharmacy-facility-${index}`,
              name: titleCase(name),
              address: buildPharmacyAddress(row),
              rawAddress: [row.province, row.Country].filter(Boolean).join(", "),
              lat,
              lng,
              country: String(row.Country || "").trim(),
              province: String(row.province || "").trim(),
              facilityType: String(row.facility_type || "").trim(),
              afrimedCategory: String(row.afrimed_category || "").trim(),
              ownership: String(row.ownership || "").trim(),
              image: imageForIndex(index),
              rating: null,
            };
          })
          .filter(Boolean);

        if (!alive) return;
        setRows(parsed);
      } catch (loadError) {
        if (!alive) return;
        setError(loadError?.message || "Failed to load pharmacies.");
        setRows([]);
      } finally {
        if (!alive) return;
        setLoadingList(false);
      }
    }

    void loadPharmacies();
    return () => {
      alive = false;
    };
  }, []);

  const activeFilterCount =
    (filters.data?.length || 0) +
    (filters.specialty?.length || 0) +
    (filters.payment?.length || 0) +
    (filters.language?.length || 0) +
    (filters.date ? 1 : 0);

  const filtered = React.useMemo(() => {
    const q = normalizeMatchText(query);
    let list = Array.isArray(rows) ? rows : [];

    if (!q && !userCoords && patientCountry) {
      const sameCountry = list.filter(
        (facility) => normalizeMatchText(facility.country || "") === patientCountry,
      );
      if (sameCountry.length) list = sameCountry;
    }

    if (q) {
      const exactCountryMatches = list.filter(
        (facility) => normalizeMatchText(facility.country || "") === q,
      );
      if (exactCountryMatches.length) {
        list = exactCountryMatches;
      } else {
        const exactProvinceMatches = list.filter(
          (facility) => normalizeMatchText(facility.province || "") === q,
        );
        if (exactProvinceMatches.length) {
          list = exactProvinceMatches;
        } else {
          list = list.filter((facility) =>
            normalizeMatchText(
              [
                facility.name,
                facility.address,
                facility.rawAddress,
                facility.facilityType,
                facility.afrimedCategory,
                facility.country,
                facility.province,
              ].join(" "),
            ).includes(q),
          );
        }
      }
    }

    list = list.map((facility) => ({
      ...facility,
      distanceKm: userCoords
        ? haversineKm(userCoords, { lat: facility.lat, lng: facility.lng })
        : null,
    }));

    if (filters.data?.includes("near-you") && userCoords) {
      list = list.filter((facility) => facility.distanceKm <= 35);
    }

    if (userCoords) {
      list = list.sort((a, b) => {
        const aDistance = Number.isFinite(a.distanceKm) ? a.distanceKm : Number.POSITIVE_INFINITY;
        const bDistance = Number.isFinite(b.distanceKm) ? b.distanceKm : Number.POSITIVE_INFINITY;
        return (
          aDistance - bDistance ||
          String(a.country || "").localeCompare(String(b.country || "")) ||
          String(a.province || "").localeCompare(String(b.province || "")) ||
          a.name.localeCompare(b.name)
        );
      });
    } else {
      list = list.sort(
        (a, b) =>
          String(a.country || "").localeCompare(String(b.country || "")) ||
          String(a.province || "").localeCompare(String(b.province || "")) ||
          a.name.localeCompare(b.name),
      );
    }

    return list.slice(0, 200);
  }, [filters.data, patientCountry, query, rows, userCoords]);

  const cards = React.useMemo(
    () =>
      filtered.map((facility) => ({
        ...facility,
        distance: formatDistance(facility.distanceKm),
      })),
    [filtered],
  );

  const mapCenter = React.useMemo(() => {
    if (
      selectedFacility &&
      Number.isFinite(Number(selectedFacility.lat)) &&
      Number.isFinite(Number(selectedFacility.lng))
    ) {
      return { lat: Number(selectedFacility.lat), lng: Number(selectedFacility.lng) };
    }

    if (userCoords) return userCoords;

    const firstVisible = filtered.find(
      (facility) =>
        Number.isFinite(Number(facility?.lat)) && Number.isFinite(Number(facility?.lng)),
    );
    if (firstVisible) {
      return { lat: Number(firstVisible.lat), lng: Number(firstVisible.lng) };
    }

    return DEMO_CENTER;
  }, [filtered, selectedFacility, userCoords]);

  const fitPharmaciesToMap = React.useCallback(
    (items, { includeUser = false, force = false, sampleCount = 8 } = {}) => {
      const mapLike = mapRef;
      if (!mapLike || !Array.isArray(items) || items.length === 0) return;

      const mapInstance =
        typeof mapLike.fitBounds === "function"
          ? mapLike
          : typeof mapLike.getMap === "function"
            ? mapLike.getMap()
            : null;
      if (!mapInstance || typeof mapInstance.fitBounds !== "function") return;

      const points = items
        .slice(0, sampleCount)
        .map((item) => [Number(item.lng), Number(item.lat)])
        .filter(([lng, lat]) => Number.isFinite(lat) && Number.isFinite(lng));

      if (includeUser && userCoords) {
        points.push([Number(userCoords.lng), Number(userCoords.lat)]);
      }

      if (!points.length) return;

      const key = JSON.stringify(points);
      if (!force && autoFitKeyRef.current === key) return;
      autoFitKeyRef.current = key;

      if (points.length === 1) {
        mapInstance.flyTo({
          center: points[0],
          zoom: includeUser && userCoords ? 12.5 : 13.5,
          duration: 900,
        });
        return;
      }

      let minLng = points[0][0];
      let maxLng = points[0][0];
      let minLat = points[0][1];
      let maxLat = points[0][1];

      points.forEach(([lng, lat]) => {
        minLng = Math.min(minLng, lng);
        maxLng = Math.max(maxLng, lng);
        minLat = Math.min(minLat, lat);
        maxLat = Math.max(maxLat, lat);
      });

      mapInstance.fitBounds(
        [
          [minLng, minLat],
          [maxLng, maxLat],
        ],
        {
          padding: {
            top: 120,
            right: 48,
            bottom: 280,
            left: 48,
          },
          duration: 900,
          maxZoom: 13.5,
        },
      );
    },
    [mapRef, userCoords],
  );

  React.useEffect(() => {
    if (!filtered.length || !mapRef || selectedFacility) return;
    fitPharmaciesToMap(filtered, {
      includeUser: !!userCoords && !normalizeMatchText(query),
      force: !!normalizeMatchText(query),
      sampleCount: normalizeMatchText(query) ? Math.min(filtered.length, 32) : 8,
    });
  }, [filtered, fitPharmaciesToMap, mapRef, query, selectedFacility, userCoords]);

  React.useEffect(() => {
    if (!selectedFacility) return;
    const stillVisible = filtered.some((facility) => facility.id === selectedFacility.id);
    if (!stillVisible) {
      setSelectedFacility(null);
      setHighlightFacilityId(null);
    }
  }, [filtered, selectedFacility]);

  React.useEffect(() => {
    if (!normalizeMatchText(query)) return;
    setSelectedFacility(null);
    setHighlightFacilityId(null);
  }, [query]);

  const handleNearMe = React.useCallback(async () => {
    if (userCoords && mapRef) {
      mapRef.flyTo({
        center: [Number(userCoords.lng), Number(userCoords.lat)],
        zoom: 13,
        duration: 900,
      });
      return;
    }

    if (geo.needsConsent) {
      geo.openDialog();
      return;
    }

    if (geo.status === "denied" || geo.consent === "denied") return;
    geo.getOnce();
  }, [geo, mapRef, userCoords]);

  const locatePharmacy = React.useCallback(
    (card) => {
      if (!mapRef) return;
      if (!Number.isFinite(Number(card?.lat)) || !Number.isFinite(Number(card?.lng))) return;

      setSelectedFacility(card);
      setHighlightFacilityId(card.id);
      window.setTimeout(() => setHighlightFacilityId(null), 1200);

      mapRef.flyTo({
        center: [Number(card.lng), Number(card.lat)],
        zoom: 15,
        duration: 900,
      });
    },
    [mapRef],
  );

  const handleFacilitySelect = React.useCallback((facility) => {
    if (!facility) return;
    setSelectedFacility(facility);
    setHighlightFacilityId(facility.id || null);
    window.setTimeout(() => setHighlightFacilityId(null), 1200);
  }, []);

  return (
    <div>
      <div className="fixed inset-0">
        <DoctorMap
          diagnosticSurface="pharmacies-discovery"
          center={mapCenter}
          showUser={canShowUser}
          user={userCoords}
          doctors={[]}
          facilities={filtered}
          heightClass="h-full"
          frameClassName="w-full h-full"
          mapStyle={getAfriMedMapStyle(isDark)}
          facilityIcon="/markers/afrimed-hospital.svg"
          facilityIconNearest="/markers/afrimed-nearest-hospital.svg"
          userIcon="/markers/afrimed-user.svg"
          highlightFacilityId={highlightFacilityId}
          focusTarget={selectedFacility}
          onFacilitySelect={handleFacilitySelect}
          onFacilityPopupClose={() => setSelectedFacility(null)}
          onRequireLocation={handleNearMe}
          onMapReady={(map) => setMapRef(map)}
        />
      </div>

      <div className="fixed inset-x-0 top-0 md:top-14 z-30">
        <div className="w-full max-w-[720px] mx-auto px-4 pt-3 md:pt-4">
          <SearchBar
            value={query}
            onChange={setQuery}
            placeholder={t("appointments.directory.pharmacies.search", "Search pharmacies and dispensaries")}
            leftIconSrc={ICONS.back}
            onLeftIconClick={() => nav(-1)}
            rightIconSrc={query?.trim() ? ICONS.cancel : null}
            onRightIconClick={() => setQuery("")}
            variant="solid"
          />

          <div className="chips-viewport mt-3 no-scrollbar">
            <div className="chips-row">
              <AfriPill
                label={t("appointments.directory.pharmacies.nearby", "Nearby")}
                iconLeft={ICONS.nearby}
                isActive={filters.data?.includes("near-you")}
                variant="doctor"
                value="near-you"
                onChange={(value) =>
                  setFilters((prev) => ({
                    ...prev,
                    data: toggleInArray(prev.data, value),
                  }))
                }
              />

              <AfriPill
                label={`${t("appointments.directory.common.filter", "Filter")}${activeFilterCount ? ` · ${activeFilterCount}` : ""}`}
                iconLeft={ICONS.filter}
                isActive={activeFilterCount > 0}
                variant="doctor"
                value="main"
                onChange={openSheet}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="fixed inset-x-0 bottom-0 z-20">
        <div className="afrimed-rail afrimed-rail--comfortable pt-4 pb-24">
          <div className="relative">
            {loadingList ? (
              <div className="px-1 pb-2 text-body-medium text-on-surface-var">
                {t("appointments.directory.common.loading", "Loading...")}
              </div>
            ) : null}

            {!loadingList && error ? (
              <div className="px-1 pb-2 text-body-medium text-[crimson]">{error}</div>
            ) : null}

            {!loadingList && !error && cards.length === 0 ? (
              <div className="mt-2 text-body-medium text-on-surface-var">
                {t(
                  "appointments.directory.pharmacies.empty",
                  "No pharmacy or dispensary facilities found.",
                )}
              </div>
            ) : null}

            <AppointmentsResultsRail
              items={cards}
              onSelect={locatePharmacy}
              renderCard={(pharmacy) => (
                <PharmacyResultCard pharmacy={pharmacy} onLocate={locatePharmacy} />
              )}
            />

            <button
              type="button"
              onClick={handleNearMe}
              className="
                absolute right-4 -top-14 h-11 w-11 rounded-[16px]
                bg-[rgb(var(--clr-surface-cont-highest))]
                flex items-center justify-center
                shadow-[0_10px_30px_rgba(15,23,42,0.18)]
              "
              aria-label={t("appointments.directory.common.centerMap", "Center map")}
            >
              <img
                src="/icons/doctor/near_me_filled.svg"
                alt=""
                className="h-5 w-5"
                draggable="false"
              />
            </button>
          </div>
        </div>
      </div>

      <LocationConsentDialog
        open={geo.dialogOpen}
        onClose={geo.closeDialog}
        onAllowOnce={geo.allowOnce}
        onAllowAlways={geo.allowAlways}
        onDeny={geo.deny}
      />
    </div>
  );
}
